import {
  TRACKED_FEATURES,
  trackFeedbackNoticeEvent,
} from '@cartwhl/tracking/featureTracking';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import Input from '@cartwhl/ui/atoms/Input';
import Button from '@cartwhl/ui/input/Button';
import FeedbackNotice from '@cartwhl/ui/input/FeedbackNotice';

import { useLocalStorage } from 'hooks/useLocalStorage';
import { characterPickerStore, noticesStore, projectStore } from 'stores';

const STORAGE_KEY =
  'studio.feedbackNotice.customCharacter.dismissedByCharacter';
const NOTICE_KEY = 'custom_character';

export default function CustomCharacterFeedbackNotice() {
  const { characters } = useSnapshot(characterPickerStore.state);
  const { currentProject } = useSnapshot(projectStore.state);
  const [dismissedByCharacter, setDismissedByCharacter] = useLocalStorage<
    Record<string, boolean>
  >(STORAGE_KEY, {});
  const trackedShownCharacterIds = useRef(new Set<string>());
  const [selectedResponse, setSelectedResponse] = useState<
    'close_not_quite' | 'not_expected' | null
  >(null);
  const [followUpFeedback, setFollowUpFeedback] = useState('');

  const selectedProjectCharacterId = useMemo(() => {
    const projectCharacters = currentProject.data?.characters;
    if (!projectCharacters) return undefined;
    const characterInstanceID = Object.keys(projectCharacters)[0];
    if (!characterInstanceID) return undefined;
    return projectCharacters[characterInstanceID]?.characterID;
  }, [currentProject.data?.characters]);

  const selectedCharacter = useMemo(
    () =>
      characters?.find(
        (character) => character.jobID === selectedProjectCharacterId,
      ),
    [characters, selectedProjectCharacterId],
  );

  const isDismissedForSelectedCharacter = Boolean(
    selectedProjectCharacterId &&
      dismissedByCharacter[selectedProjectCharacterId],
  );

  const showNotice =
    !isDismissedForSelectedCharacter &&
    Boolean(
      selectedCharacter &&
        selectedCharacter.createdBy !== 'FREE_CHARACTERS' &&
        selectedCharacter.jobID.startsWith('char-upload-'),
    );

  const dismissSelectedCharacterNotice = () => {
    if (!selectedProjectCharacterId) return;
    setDismissedByCharacter({
      ...dismissedByCharacter,
      [selectedProjectCharacterId]: true,
    });
  };

  useEffect(() => {
    if (!showNotice || !selectedProjectCharacterId) return;
    if (trackedShownCharacterIds.current.has(selectedProjectCharacterId))
      return;
    trackedShownCharacterIds.current.add(selectedProjectCharacterId);
    trackFeedbackNoticeEvent(TRACKED_FEATURES.FEEDBACK_NOTICE, 'shown', {
      character_id: selectedProjectCharacterId,
      metadata: {
        notice_key: NOTICE_KEY,
      },
    });
  }, [selectedProjectCharacterId, showNotice]);

  if (!showNotice || !selectedProjectCharacterId) {
    return null;
  }

  const dismissNoticeWithSuccess = () => {
    noticesStore.success('Feedback collected!');
    dismissSelectedCharacterNotice();
    setSelectedResponse(null);
    setFollowUpFeedback('');
  };

  const trackResponseSelection = (
    response: 'yes' | 'close_not_quite' | 'not_expected',
  ) => {
    trackFeedbackNoticeEvent(
      TRACKED_FEATURES.FEEDBACK_NOTICE,
      'option_selected',
      {
        character_id: selectedProjectCharacterId,
        metadata: {
          notice_key: NOTICE_KEY,
          response,
        },
      },
    );
  };

  const submitImmediateResponse = (response: 'yes') => {
    trackResponseSelection(response);
    dismissNoticeWithSuccess();
  };

  const finalizeFollowUpFeedback = (source: 'submit' | 'dismiss') => {
    if (!selectedResponse) return;
    const feedback = followUpFeedback.trim();
    trackFeedbackNoticeEvent(
      TRACKED_FEATURES.FEEDBACK_NOTICE,
      'response_submitted',
      {
        character_id: selectedProjectCharacterId,
        metadata: {
          notice_key: NOTICE_KEY,
          response: selectedResponse,
          feedback_submit_source: source,
          feedback_text: feedback,
          feedback_text_length: feedback.length,
          feedback_text_present: feedback.length > 0,
        },
      },
    );
    dismissNoticeWithSuccess();
  };

  return (
    <div className="absolute top-0 z-50 flex w-full justify-center">
      <FeedbackNotice
        question="Does your uploaded character look the way you imagined?"
        answers={
          selectedResponse
            ? []
            : [
                {
                  id: 'love-it',
                  label: 'Yes, love it',
                  onSelect: () => submitImmediateResponse('yes'),
                },
                {
                  id: 'close-not-quite',
                  label: 'Close, but not quite',
                  onSelect: () => {
                    setSelectedResponse('close_not_quite');
                    trackResponseSelection('close_not_quite');
                  },
                },
                {
                  id: 'not-expected',
                  label: 'Not what I expected',
                  onSelect: () => {
                    setSelectedResponse('not_expected');
                    trackResponseSelection('not_expected');
                  },
                },
              ]
        }
        belowAnswers={
          selectedResponse ? (
            <div className="relative">
              <Input
                id="feedback-notice-input"
                name="feedback-notice-input"
                as="textarea"
                autoResize
                placeholder="What should we improve?"
                value={followUpFeedback}
                onChange={(e) => setFollowUpFeedback(e.target.value)}
                className="pr-12"
              />
              <Button
                size="sm"
                variant="primary"
                onClick={() => finalizeFollowUpFeedback('submit')}
                className="absolute right-3 bottom-3"
              >
                Send
              </Button>
            </div>
          ) : null
        }
        onDismiss={() => {
          if (selectedResponse) {
            finalizeFollowUpFeedback('dismiss');
            return;
          }
          trackFeedbackNoticeEvent(
            TRACKED_FEATURES.FEEDBACK_NOTICE,
            'dismissed',
            {
              character_id: selectedProjectCharacterId,
              metadata: {
                notice_key: NOTICE_KEY,
                response: 'dismissed',
              },
            },
          );
          dismissSelectedCharacterNotice();
        }}
      />
    </div>
  );
}
